import React, { useState, useEffect } from "react";
import Sidebar from "../../components/commons/Sidebar";
import Swal from "sweetalert2";
import { getComments, approveComment, deleteComment } from "../../api/blogAPI";

const CommentsAdmin = () => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending"); // pending | approved | all

  const load = async () => {
    setLoading(true);
    try {
      const data = await getComments();
      setComments(Array.isArray(data) ? data : []);
    } catch {
      Swal.fire({ icon: "error", title: "Failed to load comments" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleApprove = async (id, approve) => {
    try {
      await approveComment(id, approve);
      setComments((prev) => prev.map((c) => c.id === id ? { ...c, is_approved: approve ? 1 : 0 } : c));
    } catch {
      Swal.fire({ icon: "error", text: "Failed to update comment status." });
    }
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: "Delete this comment?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
    }).then(async ({ isConfirmed }) => {
      if (!isConfirmed) return;
      try {
        await deleteComment(id);
        setComments((prev) => prev.filter((c) => c.id !== id));
        Swal.fire({ icon: "success", title: "Deleted!", timer: 1000, showConfirmButton: false });
      } catch {
        Swal.fire({ icon: "error", text: "Failed to delete." });
      }
    });
  };

  const filtered = comments.filter((c) => {
    if (filter === "pending") return !c.is_approved;
    if (filter === "approved") return !!c.is_approved;
    return true;
  });

  const pendingCount = comments.filter((c) => !c.is_approved).length;

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-gray-900">
      <Sidebar />
      <div className="flex-1 p-6 lg:p-10 overflow-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-indigo-400">Management</p>
            <h2 className="text-3xl font-black text-white">
              COMMENTS
              {pendingCount > 0 && (
                <span className="ml-3 text-sm font-bold bg-yellow-500/20 text-yellow-400 px-2 py-1 rounded-lg border border-yellow-500/30">
                  {pendingCount} pending
                </span>
              )}
            </h2>
          </div>
        </div>

        {/* Filter tabs */}
        <div className="flex gap-2 mb-6">
          {[
            { key: "pending", label: "Pending", count: comments.filter((c) => !c.is_approved).length },
            { key: "approved", label: "Approved", count: comments.filter((c) => c.is_approved).length },
            { key: "all", label: "All", count: comments.length },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key)}
              className={`px-4 py-2 text-sm font-bold rounded-xl transition-colors ${
                filter === tab.key
                  ? "bg-indigo-600 text-white"
                  : "bg-zinc-800 text-zinc-400 hover:text-white"
              }`}
            >
              {tab.label}
              <span className={`ml-2 text-xs px-1.5 py-0.5 rounded-full ${filter === tab.key ? "bg-indigo-500" : "bg-zinc-700"}`}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-zinc-500">Loading...</p>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-zinc-600">
            {filter === "pending" ? "No pending comments." : "No comments."}
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((c) => (
              <div key={c.id} className="bg-zinc-900/40 border border-zinc-800 rounded-2xl p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <span className="text-white font-bold text-sm">{c.name}</span>
                      <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${
                        c.is_approved ? "bg-emerald-500/20 text-emerald-400" : "bg-yellow-500/20 text-yellow-400"
                      }`}>
                        {c.is_approved ? "Approved" : "Pending"}
                      </span>
                      {c.post_title && (
                        <span className="text-[10px] text-zinc-500 truncate">
                          on: <span className="text-zinc-400">{c.post_title}</span>
                        </span>
                      )}
                      <span className="text-[10px] text-zinc-600 ml-auto font-mono">
                        {new Date(c.created_at).toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric" })}
                      </span>
                    </div>
                    <p className="text-zinc-300 text-sm leading-relaxed">{c.content}</p>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    {!c.is_approved ? (
                      <button
                        onClick={() => handleApprove(c.id, true)}
                        className="px-3 py-1.5 bg-emerald-600/20 hover:bg-emerald-600 text-emerald-400 hover:text-white text-xs font-bold rounded-lg transition-colors border border-emerald-600/30 hover:border-emerald-600"
                      >
                        Approve
                      </button>
                    ) : (
                      <button
                        onClick={() => handleApprove(c.id, false)}
                        className="px-3 py-1.5 bg-zinc-800 hover:bg-yellow-600/20 text-zinc-400 hover:text-yellow-400 text-xs font-bold rounded-lg transition-colors"
                      >
                        Unapprove
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(c.id)}
                      className="px-3 py-1.5 bg-zinc-800 hover:bg-red-600 text-zinc-400 hover:text-white text-xs font-bold rounded-lg transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CommentsAdmin;
